import type { FontMonoKey, FontSansKey } from '@pm/schemas';

export type FontKind = 'sans' | 'mono';

export interface FontEntry {
  key: FontSansKey | FontMonoKey;
  label: string;
  kind: FontKind;
  condensed?: boolean;
  /**
   * Fonte do sistema operacional (licença não permite empacotar). Não baixa nada; onde ela não
   * existe, o fallback do token entra no lugar.
   */
  system?: boolean;
  /** Import dinâmico do CSS self-hosted (@fontsource). Só baixa quando selecionada. */
  load: () => Promise<unknown>;
}

const all = (loads: (() => Promise<unknown>)[]) => () => Promise.all(loads.map((l) => l()));

/** Catálogo (skill frontend-design → references/typography.md §3). */
export const FONT_CATALOG: FontEntry[] = [
  // Sans
  {
    key: 'bricolage-grotesque',
    label: 'Bricolage Grotesque',
    kind: 'sans',
    load: () => import('@fontsource-variable/bricolage-grotesque'),
  },
  {
    key: 'quicksand',
    label: 'Quicksand',
    kind: 'sans',
    load: () => import('@fontsource-variable/quicksand'),
  },
  {
    key: 'ibm-plex-sans',
    label: 'IBM Plex Sans',
    kind: 'sans',
    load: all([
      () => import('@fontsource/ibm-plex-sans/400.css'),
      () => import('@fontsource/ibm-plex-sans/500.css'),
      () => import('@fontsource/ibm-plex-sans/600.css'),
    ]),
  },
  {
    key: 'be-vietnam-pro',
    label: 'Be Vietnam Pro',
    kind: 'sans',
    load: all([
      () => import('@fontsource/be-vietnam-pro/400.css'),
      () => import('@fontsource/be-vietnam-pro/500.css'),
      () => import('@fontsource/be-vietnam-pro/600.css'),
    ]),
  },
  {
    key: 'google-sans',
    label: 'Google Sans',
    kind: 'sans',
    load: () => import('@fontsource-variable/google-sans'),
  },
  {
    key: 'smooch-sans',
    label: 'Smooch Sans',
    kind: 'sans',
    condensed: true,
    load: all([
      () => import('@fontsource/smooch-sans/400.css'),
      () => import('@fontsource/smooch-sans/500.css'),
    ]),
  },
  {
    key: 'share-tech',
    label: 'Share Tech',
    kind: 'sans',
    load: () => import('@fontsource/share-tech'),
  },
  {
    key: 'barlow-condensed',
    label: 'Barlow Condensed',
    kind: 'sans',
    condensed: true,
    load: all([
      () => import('@fontsource/barlow-condensed/400.css'),
      () => import('@fontsource/barlow-condensed/500.css'),
      () => import('@fontsource/barlow-condensed/600.css'),
    ]),
  },
  // Mono
  {
    key: 'jetbrains-mono',
    label: 'JetBrains Mono',
    kind: 'mono',
    load: () => import('@fontsource-variable/jetbrains-mono'),
  },
  {
    key: 'ibm-plex-mono',
    label: 'IBM Plex Mono',
    kind: 'mono',
    load: all([
      () => import('@fontsource/ibm-plex-mono/400.css'),
      () => import('@fontsource/ibm-plex-mono/500.css'),
    ]),
  },
  {
    key: 'space-mono',
    label: 'Space Mono',
    kind: 'mono',
    load: () => import('@fontsource/space-mono'),
  },
  {
    key: 'fira-code',
    label: 'Fira Code',
    kind: 'mono',
    load: () => import('@fontsource-variable/fira-code'),
  },
  {
    key: 'courier-prime',
    label: 'Courier Prime',
    kind: 'mono',
    load: () => import('@fontsource/courier-prime'),
  },
  {
    key: 'google-sans-code',
    label: 'Google Sans Code',
    kind: 'mono',
    load: () => import('@fontsource-variable/google-sans-code'),
  },
  {
    key: 'share-tech-mono',
    label: 'Share Tech Mono',
    kind: 'mono',
    load: () => import('@fontsource/share-tech-mono'),
  },
  {
    key: 'fira-mono',
    label: 'Fira Mono',
    kind: 'mono',
    load: () => import('@fontsource/fira-mono'),
  },
  { key: 'lekton', label: 'Lekton', kind: 'mono', load: () => import('@fontsource/lekton') },
  // Menlo vem com o macOS. Fallbacks: DejaVu Sans Mono (Linux, mesmo desenho de origem) e Consolas (Windows).
  { key: 'menlo', label: 'Menlo', kind: 'mono', system: true, load: () => Promise.resolve() },
];

export const SANS_FONTS = FONT_CATALOG.filter((f) => f.kind === 'sans');
export const MONO_FONTS = FONT_CATALOG.filter((f) => f.kind === 'mono');

const loaded = new Set<string>(['ibm-plex-sans', 'jetbrains-mono']);

/** Carrega uma fonte uma única vez. Falha silenciosa: o fallback do token cobre. */
export async function loadFont(key: string): Promise<void> {
  if (loaded.has(key)) return;
  const entry = FONT_CATALOG.find((f) => f.key === key);
  if (!entry) return;
  loaded.add(key);
  try {
    await entry.load();
  } catch {
    loaded.delete(key);
  }
}
